import {
  Alert,
  Anchor,
  Box,
  Button,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  type ResetPasswordConfirmInput,
  resetPasswordConfirmInputSchema,
  type ResetPasswordRequestInput,
  resetPasswordRequestInputSchema,
} from '@wwaave/shared/zod';
import { useState } from 'react';
import { z } from 'zod';

import { AuthShell } from '../components/auth-shell/AuthShell';
import { useApiForm } from '../hooks/useApiForm';

const resetSearchSchema = z.object({
  token: z.string().min(16).max(256).optional(),
});

function ResetPasswordScreen() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/reset-password' });
  const token = search.token;

  if (token !== undefined) {
    return <ConfirmForm token={token} onDone={() => void navigate({ to: '/sign-in' })} />;
  }
  return <RequestForm />;
}

function RequestForm() {
  const [submitted, setSubmitted] = useState(false);
  const { form, submit, submitting } = useApiForm<ResetPasswordRequestInput>({
    endpoint: '/api/auth/reset-password/request',
    schema: resetPasswordRequestInputSchema,
    initialValues: { email: '' },
    formOptions: { mode: 'uncontrolled' },
    onSuccess: () => setSubmitted(true),
  });

  return (
    <AuthShell>
      <Stack gap="xl">
        <Box>
          <Text
            size="xs"
            tt="uppercase"
            c="brand.7"
            fw={600}
            style={{ letterSpacing: '0.1em' }}
          >
            Account recovery
          </Text>
          <Title
            order={1}
            mt={6}
            style={{
              fontSize: 'clamp(1.6rem, 3.5vw, 2.1rem)',
              lineHeight: 1.15,
              letterSpacing: '-0.015em',
            }}
          >
            Reset your password.
          </Title>
          <Text c="ink.6" mt={6} style={{ lineHeight: 1.55 }}>
            We&apos;ll email a link if an account with that address exists. The link is good for
            24 hours.
          </Text>
        </Box>

        {submitted ? (
          <Alert color="tide" variant="light">
            Check your inbox. The link is good for 24 hours.
          </Alert>
        ) : (
          <form onSubmit={submit} noValidate>
            <Stack gap="md">
              <TextInput
                label="Email"
                autoComplete="email"
                type="email"
                size="md"
                {...form.getInputProps('email')}
                key={form.key('email')}
              />
              <Button
                type="submit"
                loading={submitting}
                fullWidth
                size="md"
                variant="gradient"
                gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                mt="xs"
              >
                Send reset link
              </Button>
              <Text size="sm" ta="center">
                <Anchor component={Link} to="/sign-in" c="brand.7">
                  Back to sign in
                </Anchor>
              </Text>
            </Stack>
          </form>
        )}
      </Stack>
    </AuthShell>
  );
}

function ConfirmForm({ token, onDone }: { token: string; onDone: () => void }) {
  const { form, submit, submitting, serverError } = useApiForm<ResetPasswordConfirmInput>({
    endpoint: '/api/auth/reset-password/confirm',
    schema: resetPasswordConfirmInputSchema,
    initialValues: { token, new_password: '' },
    formOptions: { mode: 'uncontrolled' },
    onSuccess: onDone,
    mapError: (err) =>
      err.message || 'That reset link is no longer valid. Request a fresh one.',
  });

  return (
    <AuthShell>
      <Stack gap="xl">
        <Box>
          <Text
            size="xs"
            tt="uppercase"
            c="brand.7"
            fw={600}
            style={{ letterSpacing: '0.1em' }}
          >
            Account recovery
          </Text>
          <Title
            order={1}
            mt={6}
            style={{
              fontSize: 'clamp(1.6rem, 3.5vw, 2.1rem)',
              lineHeight: 1.15,
              letterSpacing: '-0.015em',
            }}
          >
            Choose a new password.
          </Title>
          <Text c="ink.6" mt={6} style={{ lineHeight: 1.55 }}>
            At least 10 characters. Memorable to you, hard for anyone else.
          </Text>
        </Box>

        <form onSubmit={submit} noValidate>
          <Stack gap="md">
            <PasswordInput
              label="New password"
              autoComplete="new-password"
              size="md"
              {...form.getInputProps('new_password')}
              key={form.key('new_password')}
            />
            {serverError !== null && (
              <Alert color="red" variant="light">
                {serverError}
              </Alert>
            )}
            <Button
              type="submit"
              loading={submitting}
              fullWidth
              size="md"
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
              mt="xs"
            >
              Save new password
            </Button>
          </Stack>
        </form>
      </Stack>
    </AuthShell>
  );
}

export const Route = createFileRoute('/reset-password')({
  validateSearch: resetSearchSchema,
  component: ResetPasswordScreen,
});
